import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Checkbox, 
  Select, 
  message, 
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Tag,
  Modal,
  Form,
  Switch
} from 'antd';
import { 
  SaveOutlined, 
  ReloadOutlined, 
  SettingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import request from '../../../utils/request';

const { Option } = Select;

interface PermissionMatrix {
  [role: string]: {
    [resource: string]: {
      [action: string]: {
        is_allowed: boolean;
        scope: string;
      };
    };
  };
}

interface MatrixData {
  role: string;
  sub_role?: string;
  resource: string;
  action: string;
  is_allowed: boolean;
  scope: string;
}

const PermissionMatrix: React.FC = () => {
  const [matrix, setMatrix] = useState<PermissionMatrix>({});
  const [matrixData, setMatrixData] = useState<MatrixData[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState<number>(20);
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [resourceFilter, setResourceFilter] = useState<string | undefined>(undefined);
  const [resourceFilterOptions, setResourceFilterOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [loadingResources, setLoadingResources] = useState<boolean>(false);
  const [onlyAllowed, setOnlyAllowed] = useState<boolean>(false);

  // 用于新增/编辑权限的角色选择
  const ROLE_CHOICES = [
    { value: 'admin', label: '管理员' },
    { value: 'staff', label: '员工' },
    { value: 'customer', label: '客户' },
  ];

  // 用于列表筛选的角色选项（客户拆分为主账号/子账号；补充员工细分角色）
  const ROLE_FILTER_CHOICES = [
    { value: 'admin', label: '管理员' },
    { value: 'hq_staff', label: '总部员工' },
    { value: 'warehouse_supervisor', label: '仓库主管' },
    { value: 'warehouse_staff', label: '仓库员工' },
    { value: 'customer_main', label: '客户主账号' },
    { value: 'customer_sub', label: '客户子账号' },
  ];

  const getSubRoleChoices = (role: string) => {
    if (role === 'staff') {
      return [
        { value: 'hq_staff', label: '总部员工' },
        { value: 'warehouse_supervisor', label: '仓库主管' },
        { value: 'warehouse_staff', label: '仓库员工' },
      ];
    }
    if (role === 'customer') {
      return [
        { value: 'main_account', label: '主账号' },
        { value: 'sub_account', label: '子账号' },
      ];
    }
    return [];
  };

  const RESOURCE_CHOICES = [
    { value: 'warehouse', label: '仓库管理' },
    { value: 'project', label: '项目管理' },
    { value: 'customer', label: '客户管理' },
    { value: 'inventory', label: '库存管理' },
    { value: 'financial', label: '财务信息' },
    { value: 'sensitive', label: '敏感信息' },
    { value: 'statistics', label: '统计信息' },
    { value: 'user', label: '用户管理' },
  ];

  const ACTION_CHOICES = [
    { value: 'create', label: '创建' },
    { value: 'read', label: '查看' },
    { value: 'update', label: '更新' },
    { value: 'delete', label: '删除' },
  ];

  const SCOPE_CHOICES = [
    { value: 'global', label: '全局' },
    { value: 'assigned', label: '分配' },
    { value: 'owned', label: '拥有' },
    { value: 'none', label: '无权限' },
  ];

  useEffect(() => {
    fetchMatrix();
    fetchResourceOptions();
  }, []);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const response = await request.get('/api/v1/permission-management/matrix/matrix/');
      setMatrix(response.data);
      
      // 转换为表格数据格式
      const data: MatrixData[] = [];
      const FLAT_ROLES = new Set(['admin', 'hq_staff', 'warehouse_supervisor', 'warehouse_staff', 'customer']);
      Object.entries(response.data).forEach(([roleKey, resources]) => {
        // 仅当 roleKey 不是已知“扁平角色”时，才尝试拆分 role_subRole
        const [role, subRole] = FLAT_ROLES.has(roleKey as string)
          ? [roleKey as string, undefined]
          : (roleKey.includes('_') ? roleKey.split('_', 2) as [string, string | undefined] : [roleKey as string, undefined]);
        
        Object.entries(resources as Record<string, any>).forEach(([resource, actions]) => {
          Object.entries(actions as Record<string, any>).forEach(([action, config]) => {
            data.push({
              role,
              sub_role: subRole,
              resource,
              action,
              is_allowed: (config as any).is_allowed,
              scope: (config as any).scope,
            });
          });
        });
      });
      
      setMatrixData(data);
    } catch (error) {
      message.error('获取权限矩阵失败');
      console.error('获取权限矩阵失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResourceOptions = async () => {
    setLoadingResources(true);
    try {
      const res = await request.get('/api/v1/permission-management/resources/', {
        params: { is_active: true, page_size: 1000 },
      });
      const raw = Array.isArray(res.data?.results)
        ? res.data.results
        : (Array.isArray(res.data) ? res.data : []);
      const opts = raw.map((item: any) => ({
        value: item.key,
        label: item.name || item.key,
      }));
      // 如果后端返回为空，保底使用本地常量
      setResourceFilterOptions(opts.length > 0 ? opts : RESOURCE_CHOICES);
    } catch (e) {
      // 失败回退本地常量
      setResourceFilterOptions(RESOURCE_CHOICES);
    } finally {
      setLoadingResources(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 转换回API格式
      const matrixToSave: PermissionMatrix = {};
      
      matrixData.forEach(item => {
        const roleKey = item.sub_role ? `${item.role}_${item.sub_role}` : item.role;
        
        if (!matrixToSave[roleKey]) {
          matrixToSave[roleKey] = {};
        }
        
        if (!matrixToSave[roleKey][item.resource]) {
          matrixToSave[roleKey][item.resource] = {};
        }
        
        matrixToSave[roleKey][item.resource][item.action] = {
          is_allowed: item.is_allowed,
          scope: item.scope,
        };
      });

      await request.post('/api/v1/permission-management/matrix/update_matrix/', {
        matrix: matrixToSave,
      });
      
      message.success('权限矩阵保存成功');
      fetchMatrix();
    } catch (error) {
      message.error('保存权限矩阵失败');
      console.error('保存权限矩阵失败:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePermissionChange = (index: number, field: keyof MatrixData, value: any) => {
    const newData = [...matrixData];
    newData[index] = { ...newData[index], [field]: value };
    setMatrixData(newData);
  };

  const handleAddPermission = () => {
    setSelectedRole('');
    form.resetFields();
    setModalVisible(true);
  };

  const handleAddSubmit = async (values: any) => {
    try {
      const newPermission: MatrixData = {
        role: values.role,
        sub_role: values.sub_role,
        resource: values.resource,
        action: values.action,
        is_allowed: values.is_allowed,
        scope: values.scope,
      };
      
      setMatrixData([...matrixData, newPermission]);
      setModalVisible(false);
      message.success('权限规则添加成功');
    } catch (error) {
      message.error('添加权限规则失败');
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: '管理员',
      staff: '员工',
      customer: '客户',
      hq_staff: '总部员工',
      warehouse_supervisor: '仓库主管',
      warehouse_staff: '仓库员工',
    };
    return roleMap[role] || role;
  };

  const getSubRoleDisplayName = (subRole: string) => {
    const subRoleMap: Record<string, string> = {
      main_account: '主账号',
      sub_account: '子账号',
      // 后端有时返回简写
      main: '主账号',
      sub: '子账号',
      hq_staff: '总部员工',
      warehouse_supervisor: '仓库主管',
      warehouse_staff: '仓库员工',
    };
    return subRoleMap[subRole] || subRole;
  };

  const getResourceDisplayName = (resource: string) => {
    const resourceMap: Record<string, string> = {
      warehouse: '仓库管理',
      project: '项目管理',
      customer: '客户管理',
      inventory: '库存管理',
      financial: '财务信息',
      sensitive: '敏感信息',
      statistics: '统计信息',
      user: '用户管理',
    };
    return resourceMap[resource] || resource;
  };

  const getActionDisplayName = (action: string) => {
    const actionMap: Record<string, string> = {
      create: '创建',
      read: '查看',
      update: '更新',
      delete: '删除',
    };
    return actionMap[action] || action;
  };

  const getScopeDisplayName = (scope: string) => {
    const scopeMap: Record<string, string> = {
      global: '全局',
      assigned: '分配',
      owned: '拥有',
      none: '无权限',
    };
    return scopeMap[scope] || scope;
  };

  // 统计数据
  const totalPermissions = matrixData.length;
  const allowedPermissions = matrixData.filter(p => p.is_allowed).length;
  const deniedPermissions = totalPermissions - allowedPermissions;

  const columns = [
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string, record: MatrixData) => {
        // 处理角色显示
        let roleDisplay = '';
        let subRoleDisplay = '';
        
        if (role === 'staff' && record.sub_role) {
          roleDisplay = '员工';
          subRoleDisplay = getSubRoleDisplayName(record.sub_role);
        } else if (role === 'customer' && record.sub_role) {
          roleDisplay = '客户';
          subRoleDisplay = getSubRoleDisplayName(record.sub_role);
        } else {
          roleDisplay = getRoleDisplayName(role);
        }
        
        return (
          <div>
            <div>{roleDisplay}</div>
            {subRoleDisplay && (
              <div style={{ fontSize: '12px', color: '#666' }}>
                ({subRoleDisplay})
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '资源',
      dataIndex: 'resource',
      key: 'resource',
      width: 120,
      render: (resource: string) => resourceLabelMap[resource] || getResourceDisplayName(resource) || resource,
    },
    {
      title: '操作',
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (action: string) => getActionDisplayName(action),
    },
    {
      title: '是否允许',
      dataIndex: 'is_allowed',
      key: 'is_allowed',
      width: 100,
      render: (isAllowed: boolean) => (
        <Tag color={isAllowed ? 'success' : 'error'}>
          {isAllowed ? '允许' : '拒绝'}
        </Tag>
      ),
    },
    {
      title: '权限范围',
      dataIndex: 'scope',
      key: 'scope',
      width: 120,
      render: (scope: string, record: MatrixData) => record.is_allowed ? getScopeDisplayName(scope) : null,
    },
  ];

  // 根据筛选条件过滤数据（前端过滤）
  const filteredData = matrixData.filter(item => {
    let matchRole = true;
    if (roleFilter) {
      if (roleFilter === 'customer_main') {
        // 兼容 main_account/main 两种子角色编码
        matchRole = item.role === 'customer' && (item.sub_role === 'main_account' || item.sub_role === 'main');
      } else if (roleFilter === 'customer_sub') {
        // 兼容 sub_account/sub 两种子角色编码
        matchRole = item.role === 'customer' && (item.sub_role === 'sub_account' || item.sub_role === 'sub');
      } else if (roleFilter === 'hq_staff' || roleFilter === 'warehouse_supervisor' || roleFilter === 'warehouse_staff') {
        // 员工细分角色：支持两种编码方式
        // 1) 直接作为角色存储：item.role === roleFilter
        // 2) staff + 子角色：item.role === 'staff' && item.sub_role === roleFilter
        matchRole = (item.role === roleFilter) || (item.role === 'staff' && item.sub_role === roleFilter);
      } else {
        // 直接按 role 匹配（admin / staff / customer 等）
        matchRole = item.role === roleFilter;
      }
    }
    const matchResource = resourceFilter ? item.resource === resourceFilter : true;
    const matchAllowed = onlyAllowed ? item.is_allowed === true : true;
    return matchRole && matchResource && matchAllowed;
  });

  // 调试：统计角色/子角色分布
  // 调试统计已移除

  // 资源 key -> 中文名 映射，来源于接口下拉
  const resourceLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    (resourceFilterOptions || []).forEach(opt => {
      if (opt && opt.value) map[opt.value] = opt.label;
    });
    return map;
  }, [resourceFilterOptions]);

  return (
    <div className="permission-matrix">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总权限规则"
              value={totalPermissions}
              prefix={<SettingOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="允许权限"
              value={allowedPermissions}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="拒绝权限"
              value={deniedPermissions}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="权限矩阵 - 使用说明"
          description={
            <div>
              <div>此处展示系统当前生效的所有“角色 × 资源 × 操作”的权限结果。</div>
              <div>如需修改，请前往“角色管理”编辑模板并应用到矩阵。</div>
              <div>可用“刷新”获取最新矩阵视图。</div>
            </div>
          }
        />
        
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Select
              allowClear
              placeholder="按角色筛选"
              style={{ width: 180 }}
              value={roleFilter}
              onChange={(val) => setRoleFilter(val)}
            >
              {ROLE_FILTER_CHOICES.map(role => (
                <Option key={role.value} value={role.value}>{role.label}</Option>
              ))}
            </Select>
            <Select
              allowClear
              placeholder="按资源筛选"
              style={{ width: 200 }}
              value={resourceFilter}
              onChange={(val) => setResourceFilter(val)}
              loading={loadingResources}
            >
              {(resourceFilterOptions.length > 0 ? resourceFilterOptions : RESOURCE_CHOICES).map(res => (
                <Option key={res.value} value={res.value}>{res.label}</Option>
              ))}
            </Select>
            <Switch
              checked={onlyAllowed}
              onChange={(v) => setOnlyAllowed(v)}
              checkedChildren="仅显示已允许"
              unCheckedChildren="显示全部"
            />
            <Button
              onClick={() => { setRoleFilter(undefined); setResourceFilter(undefined); }}
            >
              重置筛选
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchMatrix}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* 调试信息已移除 */}

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey={(record, index) => `${record.role}_${record.sub_role || ''}_${record.resource}_${record.action}`}
          pagination={{
            pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            pageSizeOptions: [10, 20, 50, 100],
            onChange: (_page, size) => {
              if (size && size !== pageSize) setPageSize(size);
            },
            onShowSizeChange: (_current, size) => {
              if (size && size !== pageSize) setPageSize(size);
            },
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title="添加权限规则"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddSubmit}
        >
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select 
              placeholder="请选择角色"
              onChange={(value) => {
                setSelectedRole(value);
                form.setFieldsValue({ sub_role: undefined }); // 清空子角色选择
              }}
            >
              {ROLE_CHOICES.map(role => (
                <Option key={role.value} value={role.value}>
                  {role.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {(selectedRole === 'staff' || selectedRole === 'customer') && (
            <Form.Item
              name="sub_role"
              label="子角色"
              rules={[{ required: true, message: '请选择子角色' }]}
            >
              <Select placeholder="请选择子角色">
                {getSubRoleChoices(selectedRole).map(subRole => (
                  <Option key={subRole.value} value={subRole.value}>
                    {subRole.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name="resource"
            label="资源"
            rules={[{ required: true, message: '请选择资源' }]}
          >
            <Select placeholder="请选择资源">
              {RESOURCE_CHOICES.map(resource => (
                <Option key={resource.value} value={resource.value}>
                  {resource.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="action"
            label="操作"
            rules={[{ required: true, message: '请选择操作' }]}
          >
            <Select placeholder="请选择操作">
              {ACTION_CHOICES.map(action => (
                <Option key={action.value} value={action.value}>
                  {action.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_allowed"
            label="是否允许"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>允许此操作</Checkbox>
          </Form.Item>

          <Form.Item
            name="scope"
            label="权限范围"
            rules={[{ required: true, message: '请选择权限范围' }]}
            initialValue="none"
          >
            <Select placeholder="请选择权限范围">
              {SCOPE_CHOICES.map(scope => (
                <Option key={scope.value} value={scope.value}>
                  {scope.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PermissionMatrix;
